import { useState, useCallback, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useReducedMotion } from 'framer-motion';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { CheckCircle, XCircle, ChevronRight, RotateCcw } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useSoundEffects } from '../../../hooks/cabin/useSoundEffects';
import { useConfetti } from '../../../hooks/cabin/useConfetti';

/**
 * DragDropSentence — drag/tap words into correct order
 *
 * phase.content schema (backend resolved):
 * {
 *   sentences: [{
 *     words: string[],          // shuffled display list
 *     correct_order: number[],  // correct index sequence into `words`
 *     instruction: string       // resolved i18n string
 *   }],
 *   points_per_sentence: number
 * }
 *
 * Legacy fallback: sentence_tr / sentence_en / sentence string → split into words
 */

function shuffleWithIds(words) {
  // Create items with stable unique ids based on position
  const items = words.map((word, idx) => ({ word, originalIdx: idx, id: `word_${idx}` }));
  // Fisher-Yates shuffle
  for (let i = items.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [items[i], items[j]] = [items[j], items[i]];
  }
  return items;
}

/**
 * Given backend words[] and correct_order[], build the correct answer string.
 * correct_order is an array of indices into words[], giving the correct sequence.
 * Example: words=["B","A","C"], correct_order=[1,0,2] → correct is ["A","B","C"]
 */
function getCorrectSentence(words, correctOrder) {
  if (!correctOrder || correctOrder.length === 0) return words.join(' ');
  return correctOrder.map((i) => words[i]).join(' ');
}

/**
 * Check if the current item order matches correct_order.
 * items is the sortable array with {originalIdx}. After sorting, items[position].originalIdx
 * should equal correct_order[position].
 */
function checkOrder(items, correctOrder) {
  if (!correctOrder || correctOrder.length === 0) {
    // Fallback: check original order (0,1,2,...)
    return items.every((item, idx) => item.originalIdx === idx);
  }
  if (items.length !== correctOrder.length) return false;
  return items.every((item, position) => item.originalIdx === correctOrder[position]);
}

function SortableWord({ item, submitted, isCorrect }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  let cls = 'border-2 border-[var(--border-light)] bg-white text-[var(--text-main)]';
  if (submitted && isCorrect) cls = 'border-2 border-[#10B981] bg-[#10B981]/10 text-emerald-700';
  if (submitted && !isCorrect) cls = 'border-2 border-[var(--brand-danger)] bg-[var(--brand-danger)]/10 text-red-700';

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`px-3 py-2 rounded-lg text-sm font-semibold cursor-grab active:cursor-grabbing select-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] ${cls}`}
      data-testid={`drag-word-${item.id}`}
      aria-grabbed={isDragging}
      aria-roledescription="draggable word"
    >
      {item.word}
    </div>
  );
}

export default function DragDropSentence({
  phase,
  onPhaseComplete,
  checkAnswer,
  'data-testid': testId = 'drag-drop-sentence',
}) {
  const { t } = useLanguage();
  const { play } = useSoundEffects();
  const { fire } = useConfetti();
  const shouldReduce = useReducedMotion();

  const sentences = phase?.content?.sentences || [];
  const maxXP = phase?.xp_in_phase || 0;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [correctCount, setCorrectCount] = useState(0);
  // Server-side grading (Bug 3)
  const collectedAnswers = useRef([]);
  const [serverResult, setServerResult] = useState(null);

  const currentSentence = sentences[currentIndex];
  const isLast = currentIndex === sentences.length - 1;

  /**
   * Resolve words array from backend schema.
   * Backend sends: { words: string[], correct_order: number[], instruction: string }
   * Legacy fallback: sentence_tr / sentence_en / sentence → split
   */
  const words = useMemo(() => {
    if (!currentSentence) return [];
    // Primary: backend schema with words array
    if (Array.isArray(currentSentence.words) && currentSentence.words.length > 0) {
      return currentSentence.words;
    }
    // Legacy fallback: sentence string
    const fallbackText =
      currentSentence.sentence_tr ||
      currentSentence.sentence_en ||
      currentSentence.sentence ||
      '';
    return fallbackText.split(' ').filter(Boolean);
  }, [currentSentence]);

  const correctOrder = useMemo(() => {
    if (!currentSentence) return null;
    if (Array.isArray(currentSentence.correct_order) && currentSentence.correct_order.length > 0) {
      return currentSentence.correct_order;
    }
    return null; // fallback: original order
  }, [currentSentence]);

  const instruction = useMemo(() => {
    if (!currentSentence) return null;
    // Backend sends resolved string in `instruction`
    return (
      currentSentence.instruction ||
      currentSentence.hint_tr ||
      currentSentence.hint ||
      null
    );
  }, [currentSentence]);

  const [items, setItems] = useState(() => shuffleWithIds(words));
  const [activeId, setActiveId] = useState(null);

  // Re-initialize when sentence changes
  const [prevIndex, setPrevIndex] = useState(currentIndex);
  if (prevIndex !== currentIndex) {
    setPrevIndex(currentIndex);
    setItems(shuffleWithIds(words));
    setSubmitted(false);
  }

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = useCallback((event) => {
    setActiveId(event.active.id);
  }, []);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    setActiveId(null);

    if (active.id !== over?.id) {
      play('click');
      setItems((prev) => {
        const oldIndex = prev.findIndex((item) => item.id === active.id);
        const newIndex = prev.findIndex((item) => item.id === over.id);
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  }, [play]);

  const isOrderCorrect = useCallback(
    (currentItems) => checkOrder(currentItems, correctOrder),
    [correctOrder]
  );

  const handleSubmit = useCallback(async () => {
    if (submitted) return;
    setSubmitted(true);

    // Kullanicinin sıralamasi: orijinal index dizisi (correct_order ile karsilastirilir)
    const userOrder = items.map((it) => it.originalIdx);
    collectedAnswers.current = [
      ...collectedAnswers.current,
      { question_index: currentIndex, order: userOrder },
    ];

    let correct;
    if (typeof checkAnswer === 'function') {
      const res = await checkAnswer({
        phaseId: phase?.phase_id,
        questionIndex: currentIndex,
        answer: { order: userOrder },
      });
      if (res) {
        setServerResult(res);
        correct = !!res.is_correct;
      } else {
        correct = isOrderCorrect(items);
      }
    } else {
      correct = isOrderCorrect(items);
    }

    if (correct) {
      play('correct');
      if (!shouldReduce) fire({ particleCount: 80 });
      setCorrectCount((c) => c + 1);
    } else {
      play('wrong');
    }
  }, [submitted, isOrderCorrect, items, currentIndex, checkAnswer, phase, play, shouldReduce, fire]);

  const handleReset = useCallback(() => {
    if (submitted) return;
    play('click');
    setItems(shuffleWithIds(words));
  }, [submitted, words, play]);

  const handleNext = useCallback(() => {
    if (isLast) {
      const score =
        sentences.length > 0
          ? Math.round((correctCount / sentences.length) * maxXP)
          : 0;
      onPhaseComplete({
        score,
        maxScore: maxXP,
        answers: collectedAnswers.current, // [{question_index, order}]
        timeSpent: 0,
      });
    } else {
      setCurrentIndex((i) => i + 1);
      setSubmitted(false);
      setServerResult(null);
    }
  }, [isLast, correctCount, sentences.length, maxXP, onPhaseComplete]);

  const isCorrect = submitted && (serverResult ? !!serverResult.is_correct : isOrderCorrect(items));
  const isWrong = submitted && !isCorrect;

  if (!currentSentence) return null;

  const correctSentence = getCorrectSentence(words, correctOrder);
  const activeItem = activeId ? items.find((i) => i.id === activeId) : null;

  return (
    <div className="flex flex-col gap-6 w-full max-w-2xl mx-auto" data-testid={testId}>
      {/* Progress */}
      <div className="flex items-center justify-between text-sm text-[var(--text-muted)]">
        <span data-testid={`${testId}-progress`}>
          {(t('exercise.question_counter') || 'Soru {current} / {total}')
            .replace('{current}', currentIndex + 1)
            .replace('{total}', sentences.length)}
        </span>
        {maxXP > 0 && (
          <span className="font-semibold text-amber-500">
            {(t('exercise.max_xp') || 'Maks. {xp} XP')
              .replace('{xp}', maxXP)}
          </span>
        )}
      </div>

      <div>
        {/* Instruction (from backend or fallback i18n) */}
        <p className="text-sm text-[var(--text-muted)] mb-3">
          {instruction || t('exercise.drag_instruction') || 'Kelimeleri doğru sıraya sürükleyin'}
        </p>

        {/* Drag area */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={items.map((i) => i.id)}
            strategy={horizontalListSortingStrategy}
          >
            <div
              className="flex flex-wrap gap-2 min-h-[56px] p-3 border-2 border-dashed border-[var(--border-light)] rounded-xl bg-gray-50"
              role="list"
              aria-label={t('exercise.word_order_area') || 'Kelime sıralama alanı'}
              data-testid={`${testId}-drop-area`}
            >
              {items.map((item) => (
                <SortableWord
                  key={item.id}
                  item={item}
                  submitted={submitted}
                  isCorrect={isCorrect}
                />
              ))}
            </div>
          </SortableContext>

          <DragOverlay>
            {activeItem && (
              <div className="px-3 py-2 rounded-lg text-sm font-semibold border-2 border-[var(--brand-primary)] bg-sky-50 text-sky-700 shadow-lg cursor-grabbing">
                {activeItem.word}
              </div>
            )}
          </DragOverlay>
        </DndContext>
      </div>

      {/* Feedback */}
      <AnimatePresence>
        {submitted && (
          <motion.div
            key="feedback"
            initial={shouldReduce ? {} : { opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
          >
            <div
              className={`flex items-start gap-3 p-4 rounded-xl border-2 ${
                isCorrect
                  ? 'border-[#10B981] bg-[#10B981]/10'
                  : 'border-[var(--brand-danger)] bg-[var(--brand-danger)]/10'
              }`}
              role="alert"
              data-testid={`${testId}-feedback`}
            >
              {isCorrect ? (
                <CheckCircle size={20} className="text-[#10B981] shrink-0 mt-0.5" aria-hidden="true" />
              ) : (
                <XCircle size={20} className="text-[var(--brand-danger)] shrink-0 mt-0.5" aria-hidden="true" />
              )}
              <div>
                <p className={`text-sm font-bold mb-1 ${isCorrect ? 'text-emerald-700' : 'text-red-700'}`}>
                  {isCorrect ? (t('exercise.correct') || 'Doğru!') : (t('exercise.wrong') || 'Yanlış!')}
                </p>
                {isWrong && (
                  <p className="text-sm text-[var(--text-muted)]">
                    {t('exercise.correct_order') || 'Doğru sıralama:'}{' '}
                    <strong>{correctSentence}</strong>
                  </p>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Buttons */}
      <div className="flex items-center justify-between">
        {!submitted && (
          <button
            onClick={handleReset}
            className="flex items-center gap-1.5 text-sm text-[var(--text-muted)] hover:text-[var(--brand-primary)] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] rounded-lg px-2 py-1"
            data-testid={`${testId}-reset`}
            aria-label={t('exercise.reset') || 'Sıfırla'}
          >
            <RotateCcw size={15} aria-hidden="true" />
            {t('exercise.reset') || 'Sıfırla'}
          </button>
        )}

        <div className="ml-auto">
          {!submitted ? (
            <motion.button
              whileHover={shouldReduce ? {} : { scale: 1.01 }}
              whileTap={shouldReduce ? {} : { scale: 0.99 }}
              onClick={handleSubmit}
              className="flex items-center gap-2 px-6 py-3 bg-[var(--brand-primary)] text-white font-bold rounded-xl shadow-[0_4px_0_0_var(--brand-primary-shadow)] hover:bg-[var(--brand-primary-hover)] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]"
              data-testid={`${testId}-submit`}
            >
              {t('exercise.submit') || 'Kontrol Et'}
            </motion.button>
          ) : (
            <motion.button
              whileHover={shouldReduce ? {} : { scale: 1.01 }}
              whileTap={shouldReduce ? {} : { scale: 0.99 }}
              onClick={handleNext}
              className="flex items-center gap-2 px-6 py-3 bg-[var(--brand-primary)] text-white font-bold rounded-xl shadow-[0_4px_0_0_var(--brand-primary-shadow)] hover:bg-[var(--brand-primary-hover)] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]"
              data-testid={`${testId}-next`}
            >
              {isLast ? (t('exercise.finish') || 'Bitir') : (t('exercise.next') || 'Sonraki')}
              <ChevronRight size={18} aria-hidden="true" />
            </motion.button>
          )}
        </div>
      </div>
    </div>
  );
}
